import Vue from 'vue'
import router from '@/router'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import UserApi from '@/api/User'
import Intercom from '@/intercom'
import KvassChatbot from '@/kvassChatbot'

import { LoadScript } from '@/utils'
import store from '@/store'

const SupportDomains = ['kvass.no', 'kvass.se', 'kvass.io']

let instance

/** Returns the current instance of the SDK */
export const getInstance = () => instance

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = function (appState) {
    router.replace(
      appState && appState.targetUrl ? appState.targetUrl.replace(location.origin, '') : window.location.pathname,
    )
  },
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        isVerified: false,
        user: {},
        auth0Client: null,
        error: null,
      }
    },
    methods: {
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o)
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o)
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o = {}) {
        if (!o.returnTo) o.returnTo = window.location.origin
        return this.auth0Client.logout({
          logoutParams: o,
        })
      },
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      let appState
      let currentRoute = router.matcher.match(location.pathname)
      if (currentRoute && currentRoute.meta.skipAuth) {
        await store.dispatch('init')
        return (this.loading = false)
      }

      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: redirectUri,
        },
      })

      let params = new URLSearchParams(window.location.search)

      if (params.get('error') === 'access_denied')
        return await this.logout({
          returnTo: window.location.protocol + '//' + window.location.host + '/401',
        })

      try {
        // If the user is returning to the app after authentication..
        if (params.has('code') && params.has('state')) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback()

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState)
        }
      } catch (e) {
        this.error = e
      } finally {
        // Initialize our internal authentication state
        let [isAuthenticated, user] = await Promise.all([
          this.auth0Client.isAuthenticated(),
          this.auth0Client.getUser(),
          store.dispatch('init'),
        ])

        this.user = user
        this.isAuthenticated = isAuthenticated

        if (!this.user) return this.loginWithRedirect({ appState: { targetUrl: window.location.href } })
        if (currentRoute && currentRoute.meta.skipVerification) return (this.loading = false)

        try {
          await UserApi.verify({ email: this.user.email })
          this.isVerified = true

          Intercom()
          KvassChatbot()
          LoadScript('/api/terms/script.js')

          if (appState) {
            const redirect = new URLSearchParams(appState.targetUrl.split('?').pop()).get('redirect')
            if (redirect) return (window.location.href = redirect)
          }
        } catch (e) {
          let name
          let path
          if (e.status === 423) {
            name = 'account-agreement-awaiting'
            path = '/account-agreement/awaiting'
          }
          if (e.status === 412) {
            const data = await e.json()
            name = 'account-agreement-sign'
            path = '/account-agreement/sign'
            window.sessionStorage.setItem('accountAgreementData', JSON.stringify(data))
          }

          if (SupportDomains.includes(this.user.email.split('@').pop())) {
            return (location.href = `/request-access?email=${this.user.email}&redirect=${currentRoute.fullPath}`)
          }

          if (name) {
            return (window.location.href = window.location.protocol + '//' + window.location.host + path)

            // TODO: Loading watcher makes this fail
            return router.push({
              name,
            })
          }

          this.error = e
          this.loading = false

          return this.logout({
            returnTo: window.location.protocol + '//' + window.location.host + '/401',
          })
        }

        this.loading = false
      }
    },
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options)
  },
}
